import React, { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Search, Loader2, CheckCircle2, AlertCircle, Download, ChevronLeft, ChevronRight, Eye, EyeOff, Key, ChevronDown, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { setWallpapers, getAllWallpapers } from '@/lib/collections/wallpapers';
import { setAdminSettings } from '@/lib/collections/adminSettings';
import { Time, Address } from '@/lib/db-client';
import { ADMIN_ADDRESS } from '@/lib/constants';
import { useAuth, getIdToken } from '@pooflabs/web';
import { AuthContextType } from '@/components/types';
import { getPoofAPIUrl, getPoofAPIHeaders } from '@/lib/config';

const WALLPAPER_CATEGORIES = ['Nature', 'Abstract', 'Minimal', 'Dark', 'Gradient', 'Space', 'Urban', 'Cyberpunk'];

interface UnsplashPhoto {
  id: string;
  urls: { full: string; regular: string; small: string };
  user: { name: string };
  description: string | null;
  alt_description: string | null;
  width: number;
  height: number;
}

type ImportStatus = 'idle' | 'importing' | 'done' | 'error' | 'duplicate';

interface ImportResult {
  id: string;
  status: ImportStatus;
  error?: string;
}

interface AdminFetchWallpapersProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigateToAdmin?: () => void;
  existingCategories?: string[];
}

export const AdminFetchWallpapers: React.FC<AdminFetchWallpapersProps> = ({ isOpen, onClose, onNavigateToAdmin, existingCategories }) => {
  const { user } = useAuth() as AuthContextType;
  const [query, setQuery] = useState('');
  const [photos, setPhotos] = useState<UnsplashPhoto[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isPopular, setIsPopular] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [hovered, setHovered] = useState<string | null>(null);
  const [defaultCategory, setDefaultCategory] = useState('Nature');
  const [defaultPrice, setDefaultPrice] = useState<number | ''>('');
  const [importResults, setImportResults] = useState<Map<string, ImportResult>>(new Map());
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [showSummary, setShowSummary] = useState(false);
  const [summaryStats, setSummaryStats] = useState({ done: 0, dup: 0, fail: 0 });
  const [apiKeyMissing, setApiKeyMissing] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [showApiKeyText, setShowApiKeyText] = useState(false);
  const [savingApiKey, setSavingApiKey] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const apiKeyInputRef = useRef<HTMLInputElement>(null);
  const allCategories = [...new Set([...WALLPAPER_CATEGORIES, ...(existingCategories ?? [])])].filter(c => c !== 'All');

  const fetchPhotos = useCallback(async (searchQuery: string, pageNum: number, popular: boolean) => {
    const token = await getIdToken();
    if (!token || !user?.address) { toast.error('Authentication required'); return; }

    setLoading(true);
    setPhotos([]);
    try {
      let endpoint: string;
      if (popular) {
        endpoint = getPoofAPIUrl(`/api/unsplash/popular?page=${pageNum}`);
      } else {
        endpoint = getPoofAPIUrl(`/api/unsplash/search?query=${encodeURIComponent(searchQuery)}&page=${pageNum}`);
      }

      const res = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Wallet-Address': user.address,
          ...getPoofAPIHeaders(),
        },
      });

      const json = await res.json() as {
        success: boolean;
        data?: { results: UnsplashPhoto[]; total: number; total_pages?: number };
        error?: { message: string };
      };

      if (!json.success || !json.data) {
        const errMsg = json.error?.message || 'Failed to fetch photos';
        // Detect API key missing errors — show inline input instead of toast
        if (errMsg.toLowerCase().includes('api key') && errMsg.toLowerCase().includes('not configured')) {
          setApiKeyMissing(true);
          return;
        }
        throw new Error(errMsg);
      }

      setApiKeyMissing(false);
      setPhotos(json.data.results);
      setTotalPages(json.data.total_pages ?? 1);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to fetch photos';
      // Also catch key errors that surface as thrown exceptions
      if (msg.toLowerCase().includes('api key') && msg.toLowerCase().includes('not configured')) {
        setApiKeyMissing(true);
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Load popular photos when modal opens
  useEffect(() => {
    if (isOpen) {
      setPage(1);
      setIsPopular(true);
      setSelected(new Set());
      setImportResults(new Map());
      setShowSummary(false);
      setApiKeyMissing(false);
      setApiKeyInput('');
      fetchPhotos('', 1, true);
    }
  }, [isOpen]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) {
      setIsPopular(true);
      setPage(1);
      fetchPhotos('', 1, true);
    } else {
      setIsPopular(false);
      setPage(1);
      fetchPhotos(q, 1, false);
    }
    setSelected(new Set());
    setImportResults(new Map());
  };

  const handleClearSearch = () => {
    setQuery('');
    setIsPopular(true);
    setPage(1);
    fetchPhotos('', 1, true);
    setSelected(new Set());
    setImportResults(new Map());
    searchInputRef.current?.focus();
  };

  const goToPage = (newPage: number) => {
    const p = Math.max(1, Math.min(newPage, totalPages));
    setPage(p);
    fetchPhotos(query.trim(), p, isPopular);
    setSelected(new Set());
  };

  const toggleSelect = (id: string) => {
    if (importResults.has(id)) return; // Already imported
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleImport = async () => {
    if (selected.size === 0 || isImporting) return;
    if (defaultPrice === '' || !(defaultPrice > 0)) { toast.error('Price must be greater than 0'); return; }

    const token = await getIdToken();
    if (!token || !user?.address) { toast.error('Authentication required'); return; }

    setIsImporting(true);
    setImportProgress(0);

    // Fetch existing wallpapers once for duplicate detection
    const existingWallpapers = await getAllWallpapers();
    const existingUrls = new Set(existingWallpapers.map(w => w.imageUrl));

    const selectedPhotos = photos.filter(p => selected.has(p.id));
    let done = 0, dup = 0, fail = 0;
    const newResults = new Map<string, ImportResult>();

    for (let i = 0; i < selectedPhotos.length; i++) {
      const photo = selectedPhotos[i];
      newResults.set(photo.id, { id: photo.id, status: 'importing' });
      setImportResults(new Map(newResults));

      try {
        // Use regular quality URL as main image, small as thumbnail
        const imageUrl = photo.urls.regular;
        const thumbnailUrl = photo.urls.small;

        if (existingUrls.has(imageUrl)) {
          newResults.set(photo.id, { id: photo.id, status: 'duplicate' });
          dup++;
        } else {
          const title = photo.alt_description || photo.description || (query.trim() ? `${query.trim()} wallpaper` : 'Unsplash wallpaper');
          const wallpaperId = `wall-unsplash-${photo.id}`;

          const ok = await setWallpapers(wallpaperId, {
            title: title.slice(0, 80),
            imageUrl,
            thumbnailUrl,
            category: defaultCategory,
            price: (defaultPrice as number) * 1_000_000,
            uploadedBy: Address.publicKey(ADMIN_ADDRESS),
            createdAt: Time.Now,
            downloadCount: 0,
            mediaType: 'wallpaper',
            status: 'published',
          } as any);

          if (ok) {
            existingUrls.add(imageUrl);
            newResults.set(photo.id, { id: photo.id, status: 'done' });
            done++;
          } else {
            newResults.set(photo.id, { id: photo.id, status: 'error', error: 'Permission denied' });
            fail++;
          }
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Unknown error';
        newResults.set(photo.id, { id: photo.id, status: 'error', error: msg });
        fail++;
      }

      setImportResults(new Map(newResults));
      setImportProgress(Math.round(((i + 1) / selectedPhotos.length) * 100));
    }

    setIsImporting(false);
    setSelected(new Set());
    setSummaryStats({ done, dup, fail });
    setShowSummary(true);
  };

  const handleSaveApiKey = async () => {
    const trimmed = apiKeyInput.trim();
    if (!trimmed) { toast.error('Please enter your Unsplash Client-Access-Key'); return; }

    setSavingApiKey(true);
    try {
      const ok = await setAdminSettings('unsplashKey', { value: trimmed, updatedAt: Time.Now });
      if (ok) {
        setApiKeyMissing(false);
        setApiKeyInput('');
        // Re-trigger the current search or popular view
        fetchPhotos(query.trim(), page, isPopular);
      } else {
        toast.error('Failed to save API key — permission denied');
      }
    } catch (err) {
      toast.error('Failed to save API key');
    } finally {
      setSavingApiKey(false);
    }
  };

  const handleClose = () => {
    if (isImporting) return;
    setQuery('');
    setPhotos([]);
    setSelected(new Set());
    setImportResults(new Map());
    setIsPopular(true);
    setPage(1);
    setShowSummary(false);
    onClose();
  };

  const selectedCount = selected.size;
  const importedCount = [...importResults.values()].filter(r => r.status === 'done').length;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          style={{
            position: 'fixed', inset: 0, zIndex: 3100,
            background: 'rgba(0,0,0,0.85)',
            backdropFilter: 'blur(16px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '16px',
          }}
          onClick={handleClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 16 }}
            transition={{ type: 'spring', stiffness: 400, damping: 32 }}
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 720,
              background: '#0a0a18',
              borderRadius: 22,
              border: '1px solid rgba(255,255,255,0.08)',
              maxHeight: 'calc(100vh - 32px)',
              display: 'flex', flexDirection: 'column',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            {/* Summary overlay */}
            <AnimatePresence>
              {showSummary && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  style={{
                    position: 'absolute', inset: 0, zIndex: 20,
                    background: 'rgba(10,10,24,0.97)',
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center', gap: 18,
                    borderRadius: '22px 22px 0 0',
                  }}
                >
                  <div style={{ fontSize: 52 }}>{summaryStats.fail === 0 ? '✨' : '⚠️'}</div>
                  <div style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: 22, color: '#f0f0f5', textAlign: 'center' }}>
                    Import Complete
                  </div>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <div style={{
                      padding: '10px 20px', borderRadius: 10,
                      background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)',
                      textAlign: 'center',
                    }}>
                      <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 26, fontWeight: 700, color: '#4ade80' }}>{summaryStats.done}</div>
                      <div style={{ fontSize: 11, color: 'rgba(240,240,245,0.5)', fontFamily: "'Syne', sans-serif", marginTop: 2 }}>Imported</div>
                    </div>
                    {summaryStats.dup > 0 && (
                      <div style={{
                        padding: '10px 20px', borderRadius: 10,
                        background: 'rgba(234,179,8,0.12)', border: '1px solid rgba(234,179,8,0.3)',
                        textAlign: 'center',
                      }}>
                        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 26, fontWeight: 700, color: '#fbbf24' }}>{summaryStats.dup}</div>
                        <div style={{ fontSize: 11, color: 'rgba(240,240,245,0.5)', fontFamily: "'Syne', sans-serif", marginTop: 2 }}>Skipped</div>
                      </div>
                    )}
                    {summaryStats.fail > 0 && (
                      <div style={{
                        padding: '10px 20px', borderRadius: 10,
                        background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)',
                        textAlign: 'center',
                      }}>
                        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 26, fontWeight: 700, color: '#f87171' }}>{summaryStats.fail}</div>
                        <div style={{ fontSize: 11, color: 'rgba(240,240,245,0.5)', fontFamily: "'Syne', sans-serif", marginTop: 2 }}>Failed</div>
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                    {summaryStats.fail > 0 && (
                      <motion.button
                        whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                        onClick={() => setShowSummary(false)}
                        style={{
                          padding: '10px 20px', borderRadius: 9,
                          background: 'rgba(255,255,255,0.06)',
                          border: '1px solid rgba(255,255,255,0.12)',
                          color: '#f0f0f5', fontSize: 13, fontWeight: 700,
                          cursor: 'pointer', fontFamily: "'Syne', sans-serif",
                        }}
                      >
                        Browse More
                      </motion.button>
                    )}
                    <motion.button
                      whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                      onClick={handleClose}
                      style={{
                        padding: '10px 28px', borderRadius: 9,
                        background: 'rgba(34,197,94,0.85)',
                        border: '1px solid rgba(34,197,94,0.5)',
                        color: '#fff', fontSize: 13, fontWeight: 700,
                        cursor: 'pointer', fontFamily: "'Syne', sans-serif",
                      }}
                    >
                      Done
                    </motion.button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Header */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '14px 16px 10px',
              borderBottom: '1px solid rgba(255,255,255,0.06)',
              flexShrink: 0,
            }}>
              {/* Back arrow button */}
              <motion.button
                whileHover={{ scale: 1.1, background: 'rgba(139,92,246,0.25)' }}
                whileTap={{ scale: 0.9 }}
                onClick={onNavigateToAdmin ?? handleClose}
                disabled={isImporting}
                title="Go back to Admin Panel"
                style={{
                  width: 36, height: 36, flexShrink: 0,
                  borderRadius: 10,
                  background: 'rgba(139,92,246,0.12)',
                  border: '1px solid rgba(139,92,246,0.25)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: isImporting ? 'not-allowed' : 'pointer',
                  color: '#a78bfa',
                  transition: 'all 0.15s ease',
                }}
              >
                <ArrowLeft size={18} />
              </motion.button>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#f0f0f5', fontFamily: "'Syne', sans-serif" }}>
                  Unsplash Library
                </div>
                <div style={{ fontSize: 10, color: 'rgba(240,240,245,0.3)', fontFamily: "'DM Mono', monospace" }}>
                  {isPopular ? 'Popular photos' : `"${query}" — ${photos.length} results`}
                  {importedCount > 0 && ` · ${importedCount} imported`}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                <motion.button
                  whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                  onClick={handleClose}
                  disabled={isImporting}
                  style={{
                    width: 28, height: 28, borderRadius: '50%',
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: isImporting ? 'not-allowed' : 'pointer',
                    color: 'rgba(240,240,245,0.6)',
                  }}
                >
                  <X size={14} />
                </motion.button>
              </div>
            </div>

            {/* Search bar */}
            <div style={{
              padding: '10px 16px',
              borderBottom: '1px solid rgba(255,255,255,0.05)',
              flexShrink: 0,
            }}>
              <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1, position: 'relative' }}>
                  <Search size={13} style={{
                    position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
                    color: 'rgba(240,240,245,0.35)', pointerEvents: 'none',
                  }} />
                  <input
                    ref={searchInputRef}
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    placeholder="Search Unsplash... (mountain, ocean, forest...)"
                    style={{
                      width: '100%', padding: '8px 10px 8px 30px',
                      borderRadius: 9, border: '1px solid rgba(255,255,255,0.1)',
                      background: 'rgba(255,255,255,0.04)', color: '#f0f0f5',
                      fontSize: 13, outline: 'none', boxSizing: 'border-box',
                      fontFamily: "'Syne', sans-serif",
                    }}
                  />
                </div>
                {query.trim() && (
                  <motion.button
                    type="button"
                    whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                    onClick={handleClearSearch}
                    style={{
                      padding: '8px 12px', borderRadius: 9,
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      color: 'rgba(240,240,245,0.5)', fontSize: 12,
                      cursor: 'pointer', fontFamily: "'Syne', sans-serif",
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Clear
                  </motion.button>
                )}
                <motion.button
                  type="submit"
                  whileHover={loading ? {} : { scale: 1.03 }} whileTap={loading ? {} : { scale: 0.97 }}
                  disabled={loading}
                  style={{
                    padding: '8px 16px', borderRadius: 9,
                    background: loading ? 'rgba(34,197,94,0.3)' : 'rgba(34,197,94,0.8)',
                    border: '1px solid rgba(34,197,94,0.4)',
                    color: '#fff', fontSize: 12, fontWeight: 700,
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontFamily: "'Syne', sans-serif",
                    display: 'flex', alignItems: 'center', gap: 6,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {loading ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Search size={13} />}
                  Search
                </motion.button>
              </form>
            </div>

            {/* Inline API key input — shown when key is missing */}
            <AnimatePresence>
              {apiKeyMissing && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  style={{ overflow: 'hidden', flexShrink: 0 }}
                >
                  <div style={{
                    margin: '8px 16px 0',
                    padding: '10px 12px',
                    borderRadius: 10,
                    background: 'rgba(234,179,8,0.07)',
                    border: '1px solid rgba(234,179,8,0.25)',
                    display: 'flex', flexDirection: 'column', gap: 8,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Key size={12} color="rgba(251,191,36,0.8)" />
                      <span style={{
                        fontSize: 11, fontWeight: 700,
                        color: 'rgba(251,191,36,0.9)',
                        fontFamily: "'Syne', sans-serif",
                        letterSpacing: '0.03em',
                      }}>
                        Unsplash API Key Required
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 7, alignItems: 'center' }}>
                      <div style={{ flex: 1, position: 'relative' }}>
                        <input
                          ref={apiKeyInputRef}
                          type={showApiKeyText ? 'text' : 'password'}
                          value={apiKeyInput}
                          onChange={e => setApiKeyInput(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && handleSaveApiKey()}
                          placeholder="Paste your Client-Access-Key..."
                          autoFocus
                          style={{
                            width: '100%',
                            padding: '7px 32px 7px 10px',
                            borderRadius: 8,
                            border: '1px solid rgba(251,191,36,0.3)',
                            background: 'rgba(0,0,0,0.3)',
                            color: '#f0f0f5',
                            fontSize: 12,
                            outline: 'none',
                            boxSizing: 'border-box',
                            fontFamily: "'DM Mono', monospace",
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowApiKeyText(p => !p)}
                          style={{
                            position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                            color: 'rgba(240,240,245,0.4)', display: 'flex', alignItems: 'center',
                          }}
                        >
                          {showApiKeyText ? <EyeOff size={13} /> : <Eye size={13} />}
                        </button>
                      </div>
                      <motion.button
                        whileHover={savingApiKey ? {} : { scale: 1.03 }}
                        whileTap={savingApiKey ? {} : { scale: 0.97 }}
                        onClick={handleSaveApiKey}
                        disabled={savingApiKey || !apiKeyInput.trim()}
                        style={{
                          padding: '7px 14px',
                          borderRadius: 8,
                          background: (savingApiKey || !apiKeyInput.trim())
                            ? 'rgba(234,179,8,0.3)'
                            : 'rgba(234,179,8,0.85)',
                          border: '1px solid rgba(234,179,8,0.4)',
                          color: (savingApiKey || !apiKeyInput.trim()) ? 'rgba(255,255,255,0.5)' : '#000',
                          fontSize: 12, fontWeight: 700,
                          cursor: (savingApiKey || !apiKeyInput.trim()) ? 'not-allowed' : 'pointer',
                          fontFamily: "'Syne', sans-serif",
                          display: 'flex', alignItems: 'center', gap: 5,
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                          transition: 'all 0.15s ease',
                        }}
                      >
                        {savingApiKey
                          ? <><Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> Saving...</>
                          : 'Save Key'
                        }
                      </motion.button>
                    </div>
                    <div style={{ fontSize: 10, color: 'rgba(240,240,245,0.3)', fontFamily: "'DM Mono', monospace" }}>
                      Get your free key at unsplash.com/developers → New Application
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Photo grid — scrollable */}
            <div style={{ flex: 1, overflowY: 'auto', padding: apiKeyMissing ? '8px 16px 12px' : '12px 16px', minHeight: 0 }}>
              {loading ? (
                /* Skeleton grid */
                <div style={{
                  display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 8,
                }}>
                  {Array.from({ length: 12 }).map((_, i) => (
                    <motion.div
                      key={i}
                      animate={{ opacity: [0.3, 0.7, 0.3] }}
                      transition={{ duration: 1.4, repeat: Infinity, delay: i * 0.08 }}
                      style={{
                        aspectRatio: '3/4',
                        borderRadius: 10,
                        background: 'rgba(255,255,255,0.06)',
                        border: '1px solid rgba(255,255,255,0.05)',
                      }}
                    />
                  ))}
                </div>
              ) : photos.length === 0 ? (
                !apiKeyMissing ? (
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  gap: 12, padding: '60px 20px',
                }}>
                  <div style={{
                    width: 56, height: 56, borderRadius: 14,
                    background: 'rgba(34,197,94,0.1)',
                    border: '1px solid rgba(34,197,94,0.2)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Search size={22} color="rgba(74,222,128,0.5)" />
                  </div>
                  <div style={{ fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: 15, color: 'rgba(240,240,245,0.6)', textAlign: 'center' }}>
                    Search for wallpapers to import
                  </div>
                  <div style={{ fontSize: 12, color: 'rgba(240,240,245,0.3)', fontFamily: "'DM Mono', monospace", textAlign: 'center' }}>
                    Try: mountain, ocean, galaxy, forest, city
                  </div>
                </div>
                ) : (
                  <div style={{ padding: '20px 0' }} />
                )
              ) : (
                <>
                  <div style={{
                    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 8,
                  }}>
                    {photos.map(photo => {
                      const isSelected = selected.has(photo.id);
                      const result = importResults.get(photo.id);
                      const isDone = result?.status === 'done';
                      const isDup = result?.status === 'duplicate';
                      const isErr = result?.status === 'error';
                      const isCurrentlyImporting = result?.status === 'importing';
                      const isHovered = hovered === photo.id;

                      return (
                        <motion.div
                          key={photo.id}
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          whileHover={isDone || isDup ? {} : { scale: 1.02 }}
                          whileTap={isDone || isDup ? {} : { scale: 0.98 }}
                          onMouseEnter={() => setHovered(photo.id)}
                          onMouseLeave={() => setHovered(null)}
                          onClick={() => toggleSelect(photo.id)}
                          style={{
                            aspectRatio: '3/4',
                            borderRadius: 10,
                            overflow: 'hidden',
                            position: 'relative',
                            cursor: (isDone || isDup || isCurrentlyImporting) ? 'default' : 'pointer',
                            border: isSelected
                              ? '2px solid rgba(34,197,94,0.9)'
                              : isDone
                                ? '2px solid rgba(34,197,94,0.5)'
                                : isDup
                                  ? '2px solid rgba(234,179,8,0.5)'
                                  : isErr
                                    ? '2px solid rgba(239,68,68,0.5)'
                                    : '2px solid transparent',
                            transition: 'border-color 0.15s ease',
                          }}
                        >
                          <img
                            src={photo.urls.small}
                            alt={photo.alt_description || photo.description || 'Unsplash photo'}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            loading="lazy"
                            decoding="async"
                          />

                          {/* Hover overlay with photographer */}
                          <AnimatePresence>
                            {isHovered && !isDone && !isDup && !isCurrentlyImporting && (
                              <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.12 }}
                                style={{
                                  position: 'absolute', inset: 0,
                                  background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 50%)',
                                  display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
                                  padding: '8px',
                                  pointerEvents: 'none',
                                }}
                              >
                                <div style={{
                                  fontSize: 9, color: 'rgba(255,255,255,0.7)',
                                  fontFamily: "'DM Mono', monospace",
                                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                }}>
                                  by {photo.user.name}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>

                          {/* Selection checkmark */}
                          <AnimatePresence>
                            {isSelected && (
                              <motion.div
                                initial={{ opacity: 0, scale: 0.7 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.7 }}
                                style={{
                                  position: 'absolute', top: 7, right: 7,
                                  width: 22, height: 22, borderRadius: '50%',
                                  background: 'rgba(34,197,94,0.9)',
                                  border: '2px solid #fff',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  pointerEvents: 'none',
                                }}
                              >
                                <CheckCircle2 size={13} color="#fff" />
                              </motion.div>
                            )}
                          </AnimatePresence>

                          {/* Import status overlays */}
                          {isCurrentlyImporting && (
                            <div style={{
                              position: 'absolute', inset: 0,
                              background: 'rgba(0,0,0,0.6)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              <Loader2 size={22} color="#4ade80" style={{ animation: 'spin 1s linear infinite' }} />
                            </div>
                          )}
                          {isDone && (
                            <div style={{
                              position: 'absolute', inset: 0,
                              background: 'rgba(0,0,0,0.45)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              <div style={{
                                padding: '5px 10px', borderRadius: 6,
                                background: 'rgba(34,197,94,0.9)',
                                color: '#fff', fontSize: 10, fontWeight: 800,
                                fontFamily: "'Syne', sans-serif",
                                display: 'flex', alignItems: 'center', gap: 4,
                              }}>
                                <CheckCircle2 size={11} />
                                Imported
                              </div>
                            </div>
                          )}
                          {isDup && (
                            <div style={{
                              position: 'absolute', inset: 0,
                              background: 'rgba(0,0,0,0.55)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              <div style={{
                                padding: '4px 8px', borderRadius: 5,
                                background: 'rgba(234,179,8,0.9)',
                                color: '#000', fontSize: 9, fontWeight: 800,
                                fontFamily: "'Syne', sans-serif",
                                textTransform: 'uppercase', letterSpacing: '0.04em',
                              }}>
                                Already exists
                              </div>
                            </div>
                          )}
                          {isErr && (
                            <div style={{
                              position: 'absolute', inset: 0,
                              background: 'rgba(0,0,0,0.55)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              <AlertCircle size={20} color="#f87171" />
                            </div>
                          )}
                        </motion.div>
                      );
                    })}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      gap: 12, marginTop: 16, paddingBottom: 4,
                    }}>
                      <motion.button
                        whileHover={page <= 1 ? {} : { scale: 1.05 }} whileTap={page <= 1 ? {} : { scale: 0.95 }}
                        onClick={() => goToPage(page - 1)}
                        disabled={page <= 1 || loading}
                        style={{
                          width: 30, height: 30, borderRadius: 8,
                          background: page <= 1 ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.08)',
                          border: '1px solid rgba(255,255,255,0.1)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          cursor: page <= 1 ? 'not-allowed' : 'pointer',
                          color: page <= 1 ? 'rgba(240,240,245,0.2)' : 'rgba(240,240,245,0.7)',
                          opacity: page <= 1 ? 0.5 : 1,
                        }}
                      >
                        <ChevronLeft size={14} />
                      </motion.button>
                      <span style={{ fontSize: 12, color: 'rgba(240,240,245,0.5)', fontFamily: "'DM Mono', monospace" }}>
                        {page} / {totalPages}
                      </span>
                      <motion.button
                        whileHover={page >= totalPages ? {} : { scale: 1.05 }} whileTap={page >= totalPages ? {} : { scale: 0.95 }}
                        onClick={() => goToPage(page + 1)}
                        disabled={page >= totalPages || loading}
                        style={{
                          width: 30, height: 30, borderRadius: 8,
                          background: page >= totalPages ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.08)',
                          border: '1px solid rgba(255,255,255,0.1)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          cursor: page >= totalPages ? 'not-allowed' : 'pointer',
                          color: page >= totalPages ? 'rgba(240,240,245,0.2)' : 'rgba(240,240,245,0.7)',
                          opacity: page >= totalPages ? 0.5 : 1,
                        }}
                      >
                        <ChevronRight size={14} />
                      </motion.button>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer — selection controls + import */}
            <div style={{
              padding: '10px 16px 14px',
              borderTop: '1px solid rgba(255,255,255,0.06)',
              flexShrink: 0,
              background: 'rgba(0,0,0,0.2)',
            }}>
              {/* Import progress bar */}
              {isImporting && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                    <span style={{ fontSize: 11, color: 'rgba(240,240,245,0.5)', fontFamily: "'DM Mono', monospace" }}>
                      Importing wallpapers...
                    </span>
                    <span style={{ fontSize: 11, color: 'rgba(74,222,128,0.8)', fontFamily: "'DM Mono', monospace" }}>
                      {importProgress}%
                    </span>
                  </div>
                  <div style={{ height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
                    <motion.div
                      animate={{ width: `${importProgress}%` }}
                      transition={{ duration: 0.4 }}
                      style={{
                        height: '100%',
                        background: 'linear-gradient(90deg, #16a34a, #4ade80)',
                        borderRadius: 2,
                      }}
                    />
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
                {/* Category selector */}
                <div style={{ flex: '1 1 140px' }}>
                  <label style={{
                    display: 'block', fontSize: 10, fontWeight: 700, color: 'rgba(240,240,245,0.4)',
                    textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4,
                    fontFamily: "'Syne', sans-serif",
                  }}>
                    Category
                  </label>
                  <div style={{ position: 'relative' }}>
                    <select
                      value={defaultCategory}
                      onChange={e => setDefaultCategory(e.target.value)}
                      style={{
                        width: '100%',
                        background: 'rgba(139,92,246,0.12)',
                        border: '1px solid rgba(139,92,246,0.35)',
                        color: '#f0f0f5', fontSize: 13, fontWeight: 700,
                        fontFamily: "'DM Mono', monospace",
                        borderRadius: 8, padding: '8px 12px',
                        appearance: 'none', paddingRight: 28,
                        outline: 'none', cursor: 'pointer', boxSizing: 'border-box',
                      }}
                    >
                      {allCategories.map(cat => (
                        <option key={cat} value={cat} style={{ background: '#0b0b17' }}>{cat}</option>
                      ))}
                    </select>
                    <ChevronDown size={12} style={{ position: 'absolute', right: 9, top: '50%', transform: 'translateY(-50%)', color: 'rgba(240,240,245,0.35)', pointerEvents: 'none' }} />
                  </div>
                </div>

                {/* Price selector */}
                <div style={{ flex: '1 1 140px' }}>
                  <label style={{
                    display: 'block', fontSize: 10, fontWeight: 700, color: 'rgba(240,240,245,0.4)',
                    textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4,
                    fontFamily: "'Syne', sans-serif",
                  }}>
                    Price
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={defaultPrice}
                    onChange={e => {
                      const raw = e.target.value;
                      if (raw === '') { setDefaultPrice(''); return; }
                      const v = parseFloat(raw);
                      if (!Number.isNaN(v) && v >= 0) setDefaultPrice(v);
                    }}
                    placeholder="Enter price"
                    style={{
                      width: '100%',
                      background: 'rgba(139,92,246,0.12)',
                      border: '1px solid rgba(139,92,246,0.35)',
                      color: '#f0f0f5',
                      fontSize: 13, fontWeight: 700,
                      fontFamily: "'DM Mono', monospace",
                      textAlign: 'center', borderRadius: 8, padding: '8px 12px',
                      WebkitAppearance: 'none', boxSizing: 'border-box', outline: 'none',
                    } as React.CSSProperties}
                  />
                </div>
              </div>

              {/* Import button */}
              <motion.button
                whileHover={(isImporting || selectedCount === 0) ? {} : { scale: 1.01, boxShadow: '0 0 20px rgba(34,197,94,0.3)' }}
                whileTap={(isImporting || selectedCount === 0) ? {} : { scale: 0.99 }}
                onClick={handleImport}
                disabled={isImporting || selectedCount === 0}
                style={{
                  width: '100%', marginTop: 10,
                  padding: '11px', borderRadius: 10,
                  background: (isImporting || selectedCount === 0) ? 'rgba(34,197,94,0.3)' : 'rgba(34,197,94,0.82)',
                  border: '1px solid rgba(74,222,128,0.4)',
                  color: '#fff', fontSize: 14, fontWeight: 700,
                  cursor: (isImporting || selectedCount === 0) ? 'not-allowed' : 'pointer',
                  fontFamily: "'Syne', sans-serif",
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  opacity: (isImporting || selectedCount === 0) ? 0.65 : 1,
                  transition: 'all 0.15s ease',
                }}
              >
                {isImporting ? (
                  <>
                    <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    Importing...
                  </>
                ) : (
                  <>
                    <Download size={15} />
                    {selectedCount > 0
                      ? `Import ${selectedCount} Wallpaper${selectedCount !== 1 ? 's' : ''}`
                      : 'Select wallpapers to import'}
                  </>
                )}
              </motion.button>

              {selectedCount > 0 && !isImporting && (
                <div style={{
                  textAlign: 'center', marginTop: 6,
                  fontSize: 10, color: 'rgba(240,240,245,0.3)',
                  fontFamily: "'DM Mono', monospace",
                }}>
                  {selectedCount} selected · {defaultCategory} · {defaultPrice !== '' ? `${defaultPrice} SKR` : '— SKR'}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
